import { useEffect, useState } from "react"
import { PropertyValueSpecification } from "maplibre-gl"
import { useMapRasterLayer } from "./useMapRasterLayer"
import { useReportOverlayStatus } from "./useReportOverlayStatus"
import { useReportOverlaySceneTime } from "./useReportOverlaySceneTime"
import { BASEMAP_RASTER_LAYER_IDS, BASEMAP_WATER_FILL_LAYER_IDS, overlaysBelow } from "./layerOrder"

const EUMETSAT_WMS = "https://view.eumetsat.int/geoserver/wms"

const LAYER_ID = "mumi:wideareacoverage_rgb_natural" as const

const TILE_PX = 512
const MAXZOOM = 7
const OPACITY = 1.0
export const GEOSTATIONARY_FADE_OUT_ZOOM = 8

const OPACITY_EXPRESSION: PropertyValueSpecification<number> = [
    "interpolate",
    ["linear"],
    ["zoom"],
    MAXZOOM,
    OPACITY,
    GEOSTATIONARY_FADE_OUT_ZOOM,
    0,
]

// wideareacoverage_rgb_natural's WMS TIME dimension steps every 3h (nearestValue snapping).
const REFRESH_MS = 3 * 60 * 60 * 1000
// EUMETSAT sometimes hasn't published a just-started epoch yet, so TIME snaps back to the
// previous scene; recheck partway through the window so we pick up the real one once it lands.
const REFETCH_MS = 15 * 60 * 1000

function currentEpoch() {
    const epoch = Math.floor(Date.now() / REFRESH_MS)
    return new Date(epoch * REFRESH_MS)
}

function buildTileUrl(layerId: string, epoch: Date, refetch: number): string {
    return (
        `${EUMETSAT_WMS}?TIME=${epoch.toISOString()}&SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap` +
        `&LAYERS=${layerId}&CRS=EPSG:3857&WIDTH=${TILE_PX}&HEIGHT=${TILE_PX}` +
        `&FORMAT=image/jpeg&BBOX={bbox-epsg-3857}&_refetch=${refetch}`
    )
}

// Discrete WMS connection parts, for the same reason noted in useFireDangerLayer.ts.
export const GEOSTATIONARY_WMS_SOURCE = {
    layerName: "Geostationary",
    baseUrl: EUMETSAT_WMS,
    wmsLayer: LAYER_ID,
}

const ABOVE = [
    ...BASEMAP_RASTER_LAYER_IDS,
    ...BASEMAP_WATER_FILL_LAYER_IDS,
    ...overlaysBelow("geostationary"),
]

export function useGeostationaryLayer(enabled: boolean) {
    const [epoch, setEpoch] = useState(currentEpoch)
    const [refetch, setRefetch] = useState(0)

    useEffect(() => {
        const id = setInterval(() => setEpoch(currentEpoch()), REFRESH_MS)
        return () => clearInterval(id)
    }, [])

    useEffect(() => {
        if (!enabled) return
        const id = setInterval(() => setRefetch((n) => n + 1), REFETCH_MS)
        return () => clearInterval(id)
    }, [enabled])

    const status = useMapRasterLayer(
        enabled,
        "geostationary",
        buildTileUrl(LAYER_ID, epoch, refetch),
        {
            maxzoom: MAXZOOM,
            opacity: OPACITY_EXPRESSION,
            tileSize: TILE_PX,
            above: ABOVE,
        }
    )
    useReportOverlayStatus("geostationary", status)
    useReportOverlaySceneTime("geostationary", epoch.getTime())
}
